import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Bell, BellRing, Check } from "lucide-react";
import { toast } from "sonner";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });
  const items = data?.items || [];
  const unread = data?.unread || 0;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/read");
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
  };

  const enablePush = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Push notifications aren't supported on this browser"); return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notification permission denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data: vapid } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid.key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      await api.post("/push/test");
      toast.success("Device alerts enabled — sent you a test notification");
    } catch (e) {
      toast.error("Could not enable alerts");
    }
  };

  return (
    <div className="relative">
      <button data-testid="notifications-bell" onClick={toggle} className="relative text-slate-400 hover:text-amber-400 transition-colors p-1.5">
        {unread > 0 ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-[#0A0D14] text-[10px] font-bold grid place-items-center">{unread}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 al-card z-50 shadow-2xl shadow-black/50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <span className="text-sm font-semibold text-slate-200">Notifications</span>
              <button data-testid="enable-push-button" onClick={enablePush} className="text-[11px] al-chip border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
                <BellRing className="w-3 h-3" /> Enable alerts
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 && <p className="px-4 py-10 text-center text-sm text-slate-500">No notifications yet. Follow an artist to get alerts on new results.</p>}
              {items.map((n) => (
                <div key={n.notif_id} className="px-4 py-3 border-b border-slate-800/50 hover:bg-slate-800/30">
                  <p className="text-sm text-slate-100 flex items-center gap-1.5">{!n.read && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}{n.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>
                  <p className="text-[10px] font-mono text-slate-600 mt-1">{new Date(n.created_at).toLocaleString("en-GB")}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
